import type { EditorStore } from '@dorian/creation-tools-ui';
import type { RenderState } from './selectRenderState';

/**
 * Converts a RenderState frame produced by EC into the vocabulary that CT's
 * EditorStore.setRenderState expects, then calls it.
 *
 * The two layers use different step index conventions:
 *   EC  — stepIndex: 0-based integer | null  (null = node open, no current step)
 *   CT  — stepNum:   1-based integer          (stepNum 1 = first step)
 *
 * Policy for empty-node frames (stepIndex === null): stepNum = 1.
 * This matches the autonomous-mode default — CT renders the background with no
 * active step, because the steps array is empty and stepNum 1 resolves to null
 * inside createToRenderData.  See controlled-viewer-contract.md §6.2.
 */
export type RenderStateDeliverer = (renderState: RenderState) => void;

// CT's BranchDetails is not re-exported from the public index, so we derive the
// first-parameter type directly from the method signature. Both types describe the
// same preview payload; EC declarations are nullable-honest per network facts while
// stock CT declarations are stricter. Runtime parity is proven by the frame diff
// tool, so this cast bridges declaration strictness, not data shape.
type CTBranchDetailsParam = Parameters<EditorStore['setRenderState']>[0];

export function createRenderStateDeliverer(store: EditorStore): RenderStateDeliverer {
  return function deliverRenderState(renderState: RenderState): void {
    const stepNum = (renderState.stepIndex ?? 0) + 1;
    store.setRenderState(renderState.node as unknown as CTBranchDetailsParam, stepNum);
  };
}
